import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Search, RefreshCw, Terminal, Plus, Pencil, Eye, X, Loader2,
    CheckCircle2, XCircle, Clock, ThumbsUp, ThumbsDown,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Send, Play, CircleEllipsis,
    ListChecks, Copy, Check
} from 'lucide-react';
import { createPortal } from 'react-dom';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { ItemsPerPageDropdown } from '../../components/ItemsPerPageDropdown ';
import CustomScriptModal from './CustomScriptModal';
import Editor from '@monaco-editor/react';
import { useClientLevel } from '../../hooks/useClientLevel';
import MultiSelectApiDropdown from '../../components/MultiSelectApiDropdown';
import { useNavigate } from 'react-router-dom';
import ColumnVisibilityDropdown, { type ColumnOption } from '../../components/ColumnVisibilityDropdown';
import { useTheme } from '../../context/ThemeContext';

interface CustomScript {
    id: number;
    name: string;
    description: string;
    script_type: string;
    content: string; // Base64 encoded
    timeout: number;
    status: string; // e.g. pending, approved, rejected
    target_os?: string;
    created?: string;
    updated?: string;
    created_by?: number;
    agent_ids?: number[];
    deployment_status?: string;
}

export type CustomScriptColumnKey =
    | 'name'
    | 'description'
    | 'script_type'
    | 'created'
    | 'timeout'
    | 'status'
    | 'actions';

// UTF-8 Safe Base64 Decode Helper
const decodeBase64 = (base64Str: string): string => {
    try {
        if (!base64Str) return '';
        return decodeURIComponent(Array.prototype.map.call(atob(base64Str), (c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
    } catch (e) {
        console.error('Error decoding from base64', e);
        return '';
    }
};

// Format ISO date string to user locale, appending 'Z' to indicate UTC if needed
const formatCreatedDate = (dateString?: string) => {
    if (!dateString) return '-';
    const cleanStr = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
    try {
        return new Date(cleanStr).toLocaleString();
    } catch (e) {
        console.error('Invalid date format:', dateString);
        return dateString;
    }
};

const CustomScripts: React.FC = () => {
    const [scripts, setScripts] = useState<CustomScript[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const level = useClientLevel();
    const clientId = level.id;
    const information = localStorage.getItem('user');
    const user_id = information ? JSON.parse(information).user_id : null;
    const navigate = useNavigate();
    const { theme } = useTheme();

    // Column visibility state
    const [visibleColumns, setVisibleColumns] = useState<Record<CustomScriptColumnKey, boolean>>({
        name: true,
        description: true,
        script_type: true,
        created: true,
        timeout: true,
        status: true,
        actions: true,
    });

    const columnOptions: ColumnOption<CustomScriptColumnKey>[] = useMemo(() => [
        { key: 'name', label: 'Script Name' },
        { key: 'description', label: 'Description' },
        { key: 'script_type', label: 'Script Type' },
        { key: 'created', label: 'Created At' },
        { key: 'timeout', label: 'Timeout (s)' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
    ], []);

    const visibleColsCount = Object.values(visibleColumns).filter(Boolean).length || 1;

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);

    // Modals
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
    const [editingScript, setEditingScript] = useState<CustomScript | null>(null);
    const [previewingScript, setPreviewingScript] = useState<CustomScript | null>(null);
    const [previewCopied, setPreviewCopied] = useState(false);

    const handleCopyPreviewCode = () => {
        if (!previewingScript) return;
        const decoded = decodeBase64(previewingScript.content);
        navigator.clipboard.writeText(decoded);
        setPreviewCopied(true);
        showSuccess('Script content copied to clipboard!');
        setTimeout(() => setPreviewCopied(false), 2000);
    };

    // Confirmation Modal for Approval/Rejection
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        scriptId: number | null;
        scriptName: string;
        action: 'approve' | 'reject' | null;
        rejectionReason?: string;
    }>({
        isOpen: false,
        scriptId: null,
        scriptName: '',
        action: null,
        rejectionReason: ''
    });
    const [actionLoading, setActionLoading] = useState(false);

    // Deploy Modal State
    const [deployModal, setDeployModal] = useState<{
        isOpen: boolean;
        scriptId: number | null;
        scriptName: string;
        selectedAgents: any[];
    }>({
        isOpen: false,
        scriptId: null,
        scriptName: '',
        selectedAgents: []
    });
    const [deployLoading, setDeployLoading] = useState(false);
    const [executeLoading, setExecuteLoading] = useState<number | null>(null);

    // Actions Dropdown Menu State
    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const menuDropdownRef = useRef<HTMLDivElement | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setDropdownPosition({
            top: rect.bottom + 1,
            left: rect.right - 160, // Align based on dropdown width w-40 (160px)
        });
        setOpenMenuId(openMenuId === id ? null : id);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target as Node)) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Fetch scripts
    const fetchScripts = async () => {
        setLoading(true);
        try {
            const params: any = {
                skip: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                query: JSON.stringify({ client_id: clientId }),
                ...(searchTerm && { search: searchTerm })
            };

            const response = await baseapi.get('/custom_scripts', { params });

            // Flexibly handle formats (if array directly, or encapsulated in data field)
            if (response.data.status) {
                setScripts(response.data.data || []);
                setTotalItems(response.data.total || (response.data.data || []).length);
            } else if (Array.isArray(response.data)) {
                setScripts(response.data);
                setTotalItems(response.data.length);
            } else if (response.data.data && Array.isArray(response.data.data)) {
                setScripts(response.data.data);
                setTotalItems(response.data.total || response.data.data.length);
            } else {
                showError('Failed to parse scripts data');
            }
        } catch (error: any) {
            console.error('Error fetching scripts:', error);
            showError(error.response?.data?.message || error.message || 'Error occurred while fetching scripts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [clientId]);

    useEffect(() => {
        fetchScripts();
    }, [currentPage, itemsPerPage, searchTerm, clientId]);

    const handleRefresh = () => {
        fetchScripts();
    };

    // Open confirmation modal for Approve/Reject
    const openConfirmModal = (script: CustomScript, action: 'approve' | 'reject') => {
        setConfirmModal({
            isOpen: true,
            scriptId: script.id,
            scriptName: script.name,
            action: action,
            rejectionReason: ''
        });
    };

    // Close confirmation modal
    const closeConfirmModal = () => {
        setConfirmModal({
            isOpen: false,
            scriptId: null,
            scriptName: '',
            action: null,
            rejectionReason: ''
        });
        setActionLoading(false);
    };

    // Execute Approve / Reject action
    const handleConfirmAction = async () => {
        const { scriptId, action, rejectionReason } = confirmModal;
        if (!scriptId || !action) return;

        if (action === 'reject' && !rejectionReason?.trim()) {
            showError('Please enter a rejection reason');
            return;
        }

        setActionLoading(true);
        try {
            const endpoint = `/custom_scripts/${scriptId}/${action}`;
            const payload = action === 'reject' ? { rejection_reason: rejectionReason } : undefined;
            const response = await baseapi.post(endpoint, payload);

            if (response.data.status) {
                showSuccess(`Script "${confirmModal.scriptName}" ${action === 'approve' ? 'approved' : 'rejected'} successfully`);
                closeConfirmModal();
                fetchScripts();
            } else {
                showError(response.data.message || `Failed to ${action} script`);
            }
        } catch (error: any) {
            console.error(`Error on script ${action}:`, error);
            showError(error.response?.data?.message || error.message || `An error occurred during script ${action}`);
        } finally {
            setActionLoading(false);
        }
    };

    // Confirm Deploy Action
    const handleDeployConfirm = async () => {
        if (!deployModal.scriptId) return;
        if (deployModal.selectedAgents.length === 0) {
            showError('Please select at least one agent to deploy');
            return;
        }

        setDeployLoading(true);
        try {
            const payload = {
                agent_ids: deployModal.selectedAgents.map(a => a.id)
            };
            const response = await baseapi.post(`/custom_scripts/${deployModal.scriptId}/deploy`, payload);
            if (response.data.status) {
                showSuccess(`Script "${deployModal.scriptName}" deployed successfully`);
                setDeployModal({ isOpen: false, scriptId: null, scriptName: '', selectedAgents: [] });
                fetchScripts();
            } else {
                showError(response.data.message || 'Failed to deploy script');
            }
        } catch (error: any) {
            console.error('Error deploying script:', error);
            showError(error.response?.data?.message || error.message || 'An error occurred during script deployment');
        } finally {
            setDeployLoading(false);
        }
    };

    // Execute script validation
    const handleExecuteScript = async (script: CustomScript) => {
        setExecuteLoading(script.id);
        try {
            const payload = {
                agent_ids: script.agent_ids || []
            };
            const response = await baseapi.post(`/custom_scripts/${script.id}/execute`, payload);
            if (response.data.status) {
                showSuccess(`Script execution triggered successfully`);
                fetchScripts();
            } else {
                showError(response.data.message || 'Failed to execute script');
            }
        } catch (error: any) {
            console.error('Error executing script:', error);
            showError(error.response?.data?.message || error.message || 'An error occurred during script execution');
        } finally {
            setExecuteLoading(null);
        }
    };

    // Render Status Badges
    const renderStatusBadge = (status: string) => {
        const s = status?.toLowerCase() || '';
        if (s === 'approved') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                    <CheckCircle2 size={11} />
                    Approved
                </span>
            );
        }
        if (s === 'rejected') {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wider">
                    <XCircle size={11} />
                    Rejected
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider">
                <Clock size={11} />
                Pending
            </span>
        );
    };

    // Monaco language helper for preview modal
    const getPreviewLanguage = (type: string) => {
        if (type === 'powershell') return 'powershell';
        if (type === 'batch') return 'bat';
        return 'shell';
    };

    return (
        <div className="flex flex-col bg-transparent text-gray-900 dark:text-gray-200 pb-20">
            {/* Header Area */}
            <div className="py-2 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-2">
                <div className="flex gap-2.5 items-center shrink-0">
                    <div className="bg-orange-600 p-1.5 rounded border border-orange-500/30">
                        <Terminal size={18} className="text-white" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">Custom Scripts</h1>
                        {totalItems > 0 && (
                            <span className="text-gray-700 dark:text-white font-bold text-sm sm:text-base">
                                ({totalItems})
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                    <button
                        onClick={() => navigate(`/patching/jobs?prev=scripts&tab=script`)}
                        className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white text-[11px] font-bold uppercase tracking-widest transition-all shadow-sm group shrink-0"
                    >
                        <ListChecks size={14} className="text-gray-400 group-hover:text-orange-500 dark:group-hover:text-orange-400 transition-colors" />
                        Jobs
                    </button>

                     <button
                        onClick={handleRefresh}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50 shadow-sm"
                        title="Refresh list"
                    >
                        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                    <div className="relative flex-1 min-w-[140px] sm:w-64 sm:flex-none group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 dark:group-focus-within:text-orange-400 transition-colors" />
                        <input
                            type="text"
                            placeholder="Search scripts..."
                            className="w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-all shadow-sm"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </div>

                    <ColumnVisibilityDropdown
                        columns={columnOptions}
                        visibleColumns={visibleColumns}
                        onChange={setVisibleColumns}
                    />

                    {/* Add Script Button */}
                    <button
                        onClick={() => {
                            setEditingScript(null);
                            setIsAddEditModalOpen(true);
                        }}
                        className="flex items-center gap-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded-lg transition-all shadow-lg shadow-orange-900/20 shrink-0 uppercase tracking-wider"
                    >
                        <Plus size={14} />
                        <span>Add Script</span>
                    </button>
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-gray-200 dark:border-gray-700/50">
                                {visibleColumns.name && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                        Script Name
                                    </th>
                                )}
                                {visibleColumns.description && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                        Description
                                    </th>
                                )}
                                {visibleColumns.script_type && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest whitespace-nowrap">
                                        Script Type
                                    </th>
                                )}
                                {visibleColumns.created && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                                        Created At
                                    </th>
                                )}
                                {visibleColumns.timeout && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                                        Timeout (s)
                                    </th>
                                )}
                                {visibleColumns.status && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                                        Status
                                    </th>
                                )}
                                {visibleColumns.actions && (
                                    <th className="px-4 py-2 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                                        Actions
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30">
                            {loading && scripts.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest text-[10px]">
                                        <div className="flex flex-col items-center gap-3">
                                            <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
                                            <span>Loading Scripts...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : scripts.length === 0 ? (
                                <tr>
                                    <td colSpan={visibleColsCount} className="py-16 text-center text-gray-400 dark:text-gray-500 font-bold uppercase tracking-widest text-[10px]">
                                        <div className="flex flex-col items-center gap-2">
                                            <Terminal className="w-8 h-8 text-gray-400 dark:text-gray-600 mb-1" />
                                            <span>No custom scripts found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                scripts.map((script) => (
                                    <tr key={script.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/50 transition-colors group">

                                        {/* Script Name */}
                                        {visibleColumns.name && (
                                            <td className="px-4 py-2.5 font-bold text-gray-900 dark:text-gray-100 text-[11px] whitespace-nowrap" title={script.name}>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
                                                    <span>{script.name}</span>
                                                </div>
                                            </td>
                                        )}

                                        {/* Description */}
                                        {visibleColumns.description && (
                                            <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 text-[10px] max-w-xs truncate" title={script.description || 'No description provided'}>
                                                {script.description || <span className="text-gray-400 dark:text-gray-600 italic">No description</span>}
                                            </td>
                                        )}

                                        {/* Script Type */}
                                        {visibleColumns.script_type && (
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <span className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-500/10 dark:border-indigo-500/20 dark:text-indigo-400 text-[10px] font-bold uppercase tracking-wider">
                                                    {script.script_type}
                                                </span>
                                            </td>
                                        )}

                                        {/* Created At */}
                                        {visibleColumns.created && (
                                            <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-300 text-[10px] whitespace-nowrap font-medium">
                                                {formatCreatedDate(script.created)}
                                            </td>
                                        )}

                                        {/* Timeout */}
                                        {visibleColumns.timeout && (
                                            <td className="px-4 py-2.5 text-center font-mono text-gray-600 dark:text-gray-300 text-[10px] whitespace-nowrap">
                                                {script.timeout}s
                                            </td>
                                        )}

                                        {/* Status */}
                                        {visibleColumns.status && (
                                            <td className="px-4 py-2.5 text-center whitespace-nowrap">
                                                {renderStatusBadge(script.status)}
                                            </td>
                                        )}

                                        {/* Actions */}
                                        {visibleColumns.actions && (
                                            <td className="px-4 py-2.5 whitespace-nowrap text-center text-xs font-medium relative">
                                                <div className="flex items-center justify-center space-x-2 relative">
                                                    <div className="relative">
                                                        <button
                                                            onClick={(e) => handleMenuOpen(e, script.id)}
                                                            className="text-gray-500 hover:text-gray-900 dark:text-white p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex items-center cursor-pointer"
                                                        >
                                                            <CircleEllipsis className="w-4 h-4" />
                                                        </button>

                                                        {openMenuId === script.id &&
                                                            createPortal(
                                                                <div
                                                                    ref={menuDropdownRef}
                                                                    className="mt-2 w-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-[9999] py-1"
                                                                    style={{
                                                                        top: dropdownPosition.top,
                                                                        left: dropdownPosition.left,
                                                                        position: "fixed",
                                                                    }}
                                                            >
                                                                {/* Preview */}
                                                                <button
                                                                    onClick={() => {
                                                                        setPreviewingScript(script);
                                                                        setOpenMenuId(null);
                                                                    }}
                                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                                                                >
                                                                    <Eye className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" />
                                                                    <span>Preview</span>
                                                                </button>

                                                                {/* Edit */}
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingScript(script);
                                                                        setIsAddEditModalOpen(true);
                                                                        setOpenMenuId(null);
                                                                    }}
                                                                    className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                                                                >
                                                                    <Pencil className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                                                    <span>Edit</span>
                                                                </button>

                                                                {/* Approve */}
                                                                
                                                                <button
                                                                    disabled={script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "approved"}
                                                                    onClick={() => {
                                                                        openConfirmModal(script, 'approve');
                                                                        setOpenMenuId(null);
                                                                    }}
                                                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                                                                        (script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "approved")
                                                                            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-40'
                                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                        }`}
                                                                    title={
                                                                        script.created_by == user_id 
                                                                            ? "You cannot approve your own script" 
                                                                            : script.status?.toLowerCase() === "approved"
                                                                                ? "Already approved"
                                                                                : "Approve"
                                                                    }
                                                                >
                                                                    <ThumbsUp className={`w-3.5 h-3.5 ${(script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "approved") ? 'text-gray-400 dark:text-gray-600' : 'text-emerald-500 dark:text-emerald-400'}`} />
                                                                    <span>Approve</span>
                                                                </button>
 
                                                                {/* Reject */}
                                                                <button
                                                                    disabled={script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "rejected"}
                                                                    onClick={() => {
                                                                        openConfirmModal(script, 'reject');
                                                                        setOpenMenuId(null);
                                                                    }}
                                                                    className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                                                                        (script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "rejected")
                                                                            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-40'
                                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                                        }`}
                                                                    title={
                                                                        script.created_by == user_id 
                                                                            ? "You cannot reject your own script" 
                                                                            : script.status?.toLowerCase() === "rejected"
                                                                                ? "Already rejected"
                                                                                : "Reject"
                                                                    }
                                                                >
                                                                    <ThumbsDown className={`w-3.5 h-3.5 ${(script.created_by == user_id || actionLoading || script.status?.toLowerCase() === "rejected") ? 'text-gray-400 dark:text-gray-600' : 'text-rose-500 dark:text-rose-400'}`} />
                                                                    <span>Reject</span>
                                                                </button>

                                                                {/* Deploy */}
                                                                {script.status?.toLowerCase() === 'approved' && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setDeployModal({ isOpen: true, scriptId: script.id, scriptName: script.name, selectedAgents: [] });
                                                                            setOpenMenuId(null);
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                                                                    >
                                                                        <Send className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                                                                        <span>Deploy</span>
                                                                    </button>
                                                                )}

                                                                {/* Execute */}
                                                                {script.deployment_status?.toLowerCase() === 'deployed' && (
                                                                    <button
                                                                        disabled={executeLoading === script.id}
                                                                        onClick={() => {
                                                                            handleExecuteScript(script);
                                                                            setOpenMenuId(null);
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors disabled:opacity-50"
                                                                    >
                                                                        {executeLoading === script.id ? (
                                                                            <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-500 dark:text-violet-400" />
                                                                        ) : (
                                                                            <Play className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                                                                        )}
                                                                        <span>Execute</span>
                                                                    </button>
                                                                )}
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                {!loading && scripts.length > 0 && (
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-700 shrink-0">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={(val) => {
                                    setItemsPerPage(val);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 tracking-widest uppercase">
                                Showing <span className="text-gray-900 dark:text-gray-200">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="text-gray-900 dark:text-gray-200">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of <span className="text-gray-900 dark:text-gray-200 font-bold">{totalItems}</span>
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(1)}
                                className="p-1.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronsLeft size={14} />
                            </button>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                className="p-1.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronLeft size={14} />
                            </button>
                            <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                                {currentPage}
                            </div>
                            <button
                                disabled={currentPage * itemsPerPage >= totalItems}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-1.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronRight size={14} />
                            </button>
                            <button
                                disabled={currentPage * itemsPerPage >= totalItems}
                                onClick={() => setCurrentPage(Math.ceil(totalItems / itemsPerPage))}
                                className="p-1.5 text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-white disabled:opacity-30"
                            >
                                <ChevronsRight size={14} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Script Modal for Add/Edit */}
            <CustomScriptModal
                isOpen={isAddEditModalOpen}
                onClose={() => {
                    setIsAddEditModalOpen(false);
                    setEditingScript(null);
                }}
                onSuccess={fetchScripts}
                editingScript={editingScript}
            />

            {/* Read-Only Preview Modal */}
            {previewingScript && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-8 animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setPreviewingScript(null)} />
                    <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl h-[75vh] relative flex flex-col overflow-hidden zoom-in-95">

                        {/* Preview Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50">
                            <div className="flex items-center gap-3">
                                <div className="bg-cyan-50 dark:bg-cyan-600/20 p-2 rounded-xl border border-cyan-200 dark:border-cyan-500/30">
                                    <Terminal size={18} className="text-cyan-600 dark:text-cyan-400" />
                                </div>
                                <div className="flex flex-col">
                                    <h2 className="text-sm font-bold text-gray-900 dark:text-white tracking-tight">Script Preview: {previewingScript.name}</h2>
                                    <span className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Type: {previewingScript.script_type} | Timeout: {previewingScript.timeout}s</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleCopyPreviewCode}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 rounded-lg text-xs font-bold text-gray-700 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white transition-all shadow-sm"
                                    title="Copy script code to clipboard"
                                >
                                    {previewCopied ? <Check size={14} className="text-emerald-500 dark:text-emerald-400" /> : <Copy size={14} className="text-gray-400" />}
                                    <span>{previewCopied ? "Copied!" : "Copy Code"}</span>
                                </button>
                                <button
                                    onClick={() => setPreviewingScript(null)}
                                    className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white bg-white hover:bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 rounded-xl transition-all shadow-sm"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {/* Preview Editor */}
                        <div className="flex-1 bg-white dark:bg-gray-950 overflow-hidden relative">
                            <Editor
                                height="100%"
                                language={getPreviewLanguage(previewingScript.script_type)}
                                theme={theme === 'dark' ? 'vs-dark' : 'light'}
                                value={decodeBase64(previewingScript.content)}
                                options={{
                                    readOnly: true,
                                    minimap: { enabled: false },
                                    fontSize: 12,
                                    lineNumbers: 'on',
                                    automaticLayout: true,
                                    scrollBeyondLastLine: false,
                                    padding: { top: 12, bottom: 12 }
                                }}
                            />
                        </div>

                        {/* Preview Footer */}
                        <div className="flex bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 p-4 justify-end shrink-0">
                            <button
                                onClick={() => setPreviewingScript(null)}
                                className="px-6 py-2 text-xs font-bold text-gray-700 dark:text-gray-300 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors uppercase tracking-wider shadow-sm"
                            >
                                Close Preview
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Approval/Rejection Confirmation Modal */}
            {confirmModal.isOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={closeConfirmModal} />
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl w-full max-w-sm relative overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6">

                            <div className="flex items-center gap-4 mb-4">
                                <div className={`p-3 rounded-xl border ${confirmModal.action === 'approve'
                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-500'
                                    : 'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-500'
                                    }`}>
                                    {confirmModal.action === 'approve' ? <ThumbsUp size={24} /> : <ThumbsDown size={24} />}
                                </div>
                                <div>
                                    <h3 className="text-base font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                                        {confirmModal.action === 'approve' ? 'Approve Script?' : 'Reject Script?'}
                                    </h3>
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                                        Are you sure you want to {confirmModal.action} the script <span className="text-gray-900 dark:text-white font-bold">"{confirmModal.scriptName}"</span>?
                                    </p>
                                </div>
                            </div>

                            {confirmModal.action === 'reject' && (
                                <div className="mt-4 flex flex-col gap-1.5">
                                    <label htmlFor="rejection-reason" className="text-[10px] font-bold uppercase tracking-wider text-gray-600 dark:text-gray-400">
                                        Rejection Reason <span className="text-rose-500">*</span>
                                    </label>
                                    <textarea
                                        id="rejection-reason"
                                        rows={3}
                                        value={confirmModal.rejectionReason}
                                        onChange={(e) => setConfirmModal(prev => ({ ...prev, rejectionReason: e.target.value }))}
                                        placeholder="Please provide the reason for rejection..."
                                        className="w-full bg-white dark:bg-gray-950 border border-gray-300 dark:border-gray-800 rounded-lg p-2.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:border-rose-500 resize-none transition-all shadow-sm"
                                    />
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    disabled={actionLoading}
                                    onClick={closeConfirmModal}
                                    className="flex-1 px-4 py-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold text-[10px] uppercase tracking-widest border border-gray-300 dark:border-gray-700 rounded-lg transition-all shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    disabled={actionLoading}
                                    className={`flex-1 px-4 py-2 text-white font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg ${confirmModal.action === 'approve'
                                        ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-950/20'
                                        : 'bg-rose-600 hover:bg-rose-500 shadow-rose-950/20'
                                        }`}
                                >
                                    {actionLoading ? (
                                        <>
                                            <Loader2 size={12} className="animate-spin" />
                                            <span>Processing...</span>
                                        </>
                                    ) : (
                                        <span>Confirm</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Deploy Modal */}
            {deployModal.isOpen && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDeployModal(prev => ({ ...prev, isOpen: false }))} />
                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl w-full max-w-md relative overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 flex flex-col gap-4">

                            <div className="flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50">
                                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                                    Deploy Script: {deployModal.scriptName}
                                </h3>
                                <button
                                    onClick={() => setDeployModal(prev => ({ ...prev, isOpen: false }))}
                                    className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="py-2">
                                <MultiSelectApiDropdown
                                    label="Select Target Agents"
                                    value={deployModal.selectedAgents}
                                    onChange={(items) => setDeployModal(prev => ({ ...prev, selectedAgents: items }))}
                                    apiEndpoint="/agents"
                                    displayProperty="name"
                                    secondaryDisplayProperty='ip_address'
                                    idProperty="id"
                                    searchFields={['name', 'ip_address']}
                                    placeholder="Search and select agents..."
                                    additionalQueries={clientId ? { client_id: clientId } : {}}
                                    required
                                />
                            </div>

                            <div className="flex gap-3 mt-4 pt-3 border-t border-gray-200 dark:border-gray-800">
                                <button
                                    disabled={deployLoading}
                                    onClick={() => setDeployModal(prev => ({ ...prev, isOpen: false }))}
                                    className="flex-1 px-4 py-2 bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold text-[10px] uppercase tracking-widest border border-gray-300 dark:border-gray-700 rounded-lg transition-all shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeployConfirm}
                                    disabled={deployLoading || deployModal.selectedAgents.length === 0}
                                    className="flex-1 px-4 py-2 text-white font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg bg-orange-600 hover:bg-orange-500 disabled:opacity-50"
                                >
                                    {deployLoading ? (
                                        <>
                                            <Loader2 size={12} className="animate-spin" />
                                            <span>Deploying...</span>
                                        </>
                                    ) : (
                                        <span>Confirm Deploy</span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomScripts;
